import {
  CustomerInvoiceDueBalance,
  getCustomerInvoiceBalance,
  getCustomerInvoiceContactLabel,
  getCustomerInvoiceLabel,
  getCustomerReceivableInvoices,
  getCustomerInvoiceRowClass,
  CustomerInvoiceStatusPill,
} from '@/components/customers/customer-invoice-display'
import type { PaymentModalMode } from '@/components/pos/PaymentModal'
import { PaymentModalLoadFallback } from '@/components/pos/PaymentModalLoadFallback'
import { PaymentModal, waitForPaymentModalCode } from '@/components/pos/payment-modal-loader'
import { PaymentMethod as ModalPaymentMethod } from '@/components/pos/payment-modal-state'
import { EmptyState } from '@/components/patterns/empty-state'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { CurrencyInput } from '@/components/ui/currency-input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import LoadingButton from '@/components/ui/loading-button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Typography } from '@/components/ui/typography'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
// oxlint-disable-next-line no-restricted-imports -- grandfathered
import { previewCustomerPaymentSnapshot } from '@/lib/services/customers/customers.api'
import type {
  Customer,
  CustomerPaymentAllocationInput,
  CustomerPaymentSnapshot,
  CustomerPaymentSnapshotSelection,
  CustomerStatement,
  CustomerStatementInvoice,
} from '@/lib/services/customers/customers.types'
import { formatCurrency, getErrorMessage, normalizeCurrencyValue, parseCurrency } from '@/lib/utils'
import { useMemo, useRef, useState } from 'react'

const CURRENCY_EPSILON = 0.009

/** Where one invoice stands with the amount applied to it. */
interface InvoiceAllocationState {
  status: 'empty' | 'partial' | 'full' | 'over'
  invoiceBalance: number
  appliedAmount: number
  remainingBalance: number
  overAppliedAmount: number
}

function formatAllocationAmount(value: CustomerPaymentSnapshot['total'] | number): string {
  const normalizedValue = normalizeCurrencyValue(value)

  if (normalizedValue <= 0) {
    return ''
  }

  return normalizedValue.toFixed(2)
}

function getInvoiceRemainingTotal(invoice: CustomerStatementInvoice): number {
  return getCustomerInvoiceBalance(invoice)
}

function getInvoiceAllocationState(
  invoice: CustomerStatementInvoice,
  allocationAmount: number
): InvoiceAllocationState {
  const invoiceBalance = getInvoiceRemainingTotal(invoice)
  const appliedAmount = normalizeCurrencyValue(allocationAmount)
  const remainingBalance = Math.max(Number((invoiceBalance - appliedAmount).toFixed(2)), 0)
  const overAppliedAmount = Math.max(Number((appliedAmount - invoiceBalance).toFixed(2)), 0)

  if (appliedAmount <= CURRENCY_EPSILON) {
    return {
      status: 'empty',
      invoiceBalance,
      appliedAmount,
      remainingBalance: invoiceBalance,
      overAppliedAmount: 0,
    }
  }

  if (overAppliedAmount > CURRENCY_EPSILON) {
    return {
      status: 'over',
      invoiceBalance,
      appliedAmount,
      remainingBalance: 0,
      overAppliedAmount,
    }
  }

  if (remainingBalance <= CURRENCY_EPSILON) {
    return {
      status: 'full',
      invoiceBalance,
      appliedAmount,
      remainingBalance: 0,
      overAppliedAmount: 0,
    }
  }

  return {
    status: 'partial',
    invoiceBalance,
    appliedAmount,
    remainingBalance,
    overAppliedAmount: 0,
  }
}

function InvoiceAllocationOutcome({ state }: { state: InvoiceAllocationState }) {
  if (state.status === 'empty') {
    return null
  }

  const config = {
    full: {
      label: 'Full balance',
      detail: 'Paid in full after this payment',
      value: '$0.00 left',
      className:
        'border-status-success-border bg-status-success-background text-status-success-foreground dark:border-status-success-border dark:bg-status-success-background dark:text-status-success-foreground',
    },
    partial: {
      label: 'Partial payment',
      detail: 'Remaining after payment',
      value: `${formatCurrency(state.remainingBalance)} left`,
      className:
        'border-status-warning-border bg-status-warning-background text-status-warning-foreground dark:border-status-warning-border dark:bg-status-warning-background dark:text-status-warning-foreground',
    },
    over: {
      label: 'Too much applied',
      detail: 'Reduce this invoice amount',
      value: `${formatCurrency(state.overAppliedAmount)} over`,
      className:
        'border-status-destructive-border bg-status-destructive-background text-status-destructive-foreground',
    },
  }[state.status]

  return (
    <div className={`rounded-md border px-2.5 py-2 text-xs ${config.className}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-semibold">{config.label}</span>
        <span className="whitespace-nowrap font-semibold tabular-nums">{config.value}</span>
      </div>
      <span className="mt-1 block">{config.detail}</span>
    </div>
  )
}

interface CustomerPaymentDialogProps {
  customer: Pick<Customer, 'id' | 'name'>
  /** The customer's statement; undefined until it loads. */
  receivables: CustomerStatement | undefined
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Pay Now's payment, frozen when Continue's preview answers, so it can't drift from the snapshot it opened on. */
interface PreviewedPayment {
  paymentSnapshot: CustomerPaymentSnapshot & {
    location_id: number | null
    manual_tender_location_blocker: string | null
  }
  allocations: CustomerPaymentAllocationInput[]
  total: CustomerPaymentSnapshot['total']
  primarySalesOrderId: number | null
}

export function CustomerPaymentDialog({
  customer,
  receivables,
  open,
  onOpenChange,
}: CustomerPaymentDialogProps) {
  const customerId = Number(customer?.id ?? 0)
  const [paymentAmountValue, setPaymentAmountValue] = useState('')
  // What staff typed for each invoice, by invoice id.
  const [allocationValues, setAllocationValues] = useState<Record<number, string>>({})
  const [paymentModalMode, setPaymentModalMode] = useState<PaymentModalMode | null>(null)
  const organizationId = useRouteOrganizationId()
  // What Continue previewed. Pay Now pays exactly this, whatever happens to the invoices behind it meanwhile.
  const [previewedPayment, setPreviewedPayment] = useState<PreviewedPayment | null>(null)
  const [isPreparingPayment, setIsPreparingPayment] = useState(false)
  const [prepareError, setPrepareError] = useState<string | null>(null)
  // The Continue waiting on the server's snapshot. Closing the dialog drops it, so its answer can't reopen Pay Now.
  const pendingPreviewRef = useRef<object | null>(null)

  const invoices = useMemo(() => getCustomerReceivableInvoices(receivables), [receivables])
  const openBalanceTotal = normalizeCurrencyValue(receivables?.summary.open_balance_total)
  const hasCollectibleInvoices = invoices.some(
    (invoice) => invoice.financial_state?.can_collect_payment !== false
  )
  const hasPaymentBootstrap = Boolean(receivables?.payment_bootstrap)
  const selectedAllocations = useMemo(
    () =>
      invoices
        .map((invoice) => {
          const amount = normalizeCurrencyValue(allocationValues[invoice.id] ?? 0)

          return {
            sales_order_id: invoice.id,
            amount: amount > 0 ? amount : 0,
          }
        })
        .filter((allocation) => allocation.amount > 0),
    [allocationValues, invoices]
  )
  const selectedTotal = selectedAllocations.reduce(
    (total, allocation) => Number((total + allocation.amount).toFixed(2)),
    0
  )
  const selectedInvoiceLocationIds = selectedAllocations
    .map((allocation) =>
      Number(
        invoices.find((invoice) => Number(invoice.id) === Number(allocation.sales_order_id))
          ?.location_id ?? 0
      )
    )
    .filter((locationId) => locationId > 0)
  const uniqueSelectedInvoiceLocationIds = [...new Set(selectedInvoiceLocationIds)]
  const selectedInvoiceLocationId =
    selectedInvoiceLocationIds.length === selectedAllocations.length &&
    uniqueSelectedInvoiceLocationIds.length === 1
      ? (uniqueSelectedInvoiceLocationIds[0] ?? null)
      : null
  const manualTenderLocationBlocker =
    selectedAllocations.length > 0 && !selectedInvoiceLocationId
      ? 'Cash and check payments can only be applied to invoices from one location. Select invoices from a single location or use a card.'
      : null
  const hasInvoiceOverAllocation = invoices.some((invoice) => {
    const allocationAmount = normalizeCurrencyValue(allocationValues[invoice.id] ?? 0)
    return getInvoiceAllocationState(invoice, allocationAmount).status === 'over'
  })
  const primarySalesOrderId = selectedAllocations[0]?.sales_order_id ?? null
  const paymentAmount = normalizeCurrencyValue(paymentAmountValue)
  const hasPaymentAmount = paymentAmount > 0
  const allocationCap = hasPaymentAmount ? Math.min(paymentAmount, openBalanceTotal) : null
  const unallocatedAmount =
    hasPaymentAmount && allocationCap !== null
      ? Math.max(Number((allocationCap - selectedTotal).toFixed(2)), 0)
      : 0
  const overAllocatedAmount =
    hasPaymentAmount && allocationCap !== null
      ? Math.max(Number((selectedTotal - allocationCap).toFixed(2)), 0)
      : 0
  const hasBalancedPaymentAmount =
    !hasPaymentAmount ||
    (unallocatedAmount <= CURRENCY_EPSILON && overAllocatedAmount <= CURRENCY_EPSILON)
  const canContinue =
    customerId > 0 &&
    invoices.length > 0 &&
    hasPaymentBootstrap &&
    selectedAllocations.length > 0 &&
    selectedTotal > 0 &&
    !hasInvoiceOverAllocation &&
    hasBalancedPaymentAmount
  const resetState = () => {
    pendingPreviewRef.current = null
    setIsPreparingPayment(false)
    setPaymentAmountValue('')
    setAllocationValues({})
    setPaymentModalMode(null)
    setPreviewedPayment(null)
    setPrepareError(null)
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      if (paymentModalMode) {
        return
      }

      resetState()
    }

    onOpenChange(nextOpen)
  }

  const handleSuccessfulPaymentClose = () => {
    resetState()
    onOpenChange(false)
  }

  const setInvoiceAllocation = (invoiceId: number, value: string) => {
    setAllocationValues((currentValues) => ({
      ...currentValues,
      [invoiceId]: value,
    }))
  }

  const applyAllocationChoice = async (
    selection: Exclude<
      CustomerPaymentSnapshotSelection,
      { allocations: CustomerPaymentAllocationInput[] }
    >
  ) => {
    const request = {}
    pendingPreviewRef.current = request
    setIsPreparingPayment(true)
    setPrepareError(null)
    try {
      const response = await previewCustomerPaymentSnapshot({
        organizationId,
        customerId,
        ...selection,
      })
      if (pendingPreviewRef.current === request) {
        setAllocationValues(
          Object.fromEntries(
            response.allocations.map((allocation) => [allocation.sales_order_id, allocation.amount])
          )
        )
        setPaymentAmountValue(formatAllocationAmount(response.payment_snapshot.total))
      }
    } catch (error) {
      if (pendingPreviewRef.current === request) {
        setPrepareError(getErrorMessage(error) || 'Unable to apply this payment. Please try again.')
      }
    } finally {
      if (pendingPreviewRef.current === request) {
        pendingPreviewRef.current = null
        setIsPreparingPayment(false)
      }
    }
  }

  const applyFullBalances = () => {
    if (!hasCollectibleInvoices) return
    return applyAllocationChoice({ selection: 'total_balance' })
  }
  const applyOldestFirst = () => {
    const targetAmount = paymentAmount > 0 ? paymentAmount : openBalanceTotal
    if (!hasCollectibleInvoices || targetAmount <= 0) return
    return applyAllocationChoice({ selection: 'oldest_first', amount: targetAmount.toFixed(2) })
  }

  const clearAllocations = () => {
    setAllocationValues({})
  }

  const toggleInvoiceSelection = (invoice: CustomerStatementInvoice) => {
    const currentAmount = normalizeCurrencyValue(allocationValues[invoice.id] ?? 0)

    if (currentAmount > 0) {
      setInvoiceAllocation(invoice.id, '')
      return
    }

    const invoiceRemaining = getInvoiceRemainingTotal(invoice)
    const availableForInvoice =
      hasPaymentAmount && allocationCap !== null
        ? Math.max(Number((allocationCap - selectedTotal).toFixed(2)), 0)
        : invoiceRemaining
    const nextAmount = Math.min(invoiceRemaining, availableForInvoice)

    if (nextAmount <= 0) {
      return
    }

    setInvoiceAllocation(invoice.id, formatAllocationAmount(nextAmount))
  }

  const handleSubmit = async () => {
    if (!canContinue || isPreparingPayment) {
      return
    }

    const preview = {}
    pendingPreviewRef.current = preview
    setIsPreparingPayment(true)
    setPrepareError(null)

    try {
      // Pay Now's code loads alongside the snapshot, so Pay Now opens straight onto its own screen.
      const [response] = await Promise.all([
        previewCustomerPaymentSnapshot({
          organizationId,
          customerId,
          allocations: selectedAllocations,
        }),
        waitForPaymentModalCode(),
      ])

      if (pendingPreviewRef.current === preview) {
        setPreviewedPayment({
          // Pay Now reads the server's snapshot. Only the invoices' shared location (for a tracked cash drawer) and
          // why cash can't be taken are the dialog's own.
          paymentSnapshot: {
            ...response.payment_snapshot,
            location_id: selectedInvoiceLocationId,
            manual_tender_location_blocker: manualTenderLocationBlocker,
          },
          allocations: response.allocations,
          total: response.payment_snapshot.total,
          primarySalesOrderId,
        })
        setPaymentModalMode('split')
      }
    } catch (error) {
      if (pendingPreviewRef.current === preview) {
        setPrepareError(getErrorMessage(error) || 'Unable to start this payment. Please try again.')
      }
    } finally {
      if (pendingPreviewRef.current === preview) {
        pendingPreviewRef.current = null
        setIsPreparingPayment(false)
      }
    }
  }

  return (
    <Dialog modal={!paymentModalMode} open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="2xlarge" viewport="contained">
        <DialogHeader>
          <DialogTitle>Take Customer Payment</DialogTitle>
          <DialogDescription>
            Allocate today&apos;s payment across this customer&apos;s open invoices.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <ScrollArea className="max-h-[calc(100dvh-14rem)] pr-4">
            {/* The invoices stay as sent while Continue waits and while Pay Now is open. */}
            <fieldset
              disabled={isPreparingPayment || paymentModalMode !== null}
              className="min-w-0 space-y-4"
            >
              <div className="space-y-3 rounded-md border bg-muted/20 p-3">
                <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-end">
                  <div className="space-y-2">
                    <Label htmlFor="customer-payment-amount">Payment amount</Label>
                    <CurrencyInput
                      id="customer-payment-amount"
                      placeholder="0.00"
                      value={paymentAmountValue}
                      onValueChange={setPaymentAmountValue}
                      onBlurValueChange={setPaymentAmountValue}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={!hasCollectibleInvoices}
                    onClick={applyOldestFirst}
                  >
                    Apply oldest first
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!hasCollectibleInvoices}
                    onClick={applyFullBalances}
                  >
                    Pay full balance
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={clearAllocations}>
                    Clear allocations
                  </Button>
                </div>

                {overAllocatedAmount > 0 ? (
                  <Typography variant="label" tone="destructive" className="block">
                    Allocations exceed the payment amount by {formatCurrency(overAllocatedAmount)}.
                  </Typography>
                ) : unallocatedAmount > 0 ? (
                  <Typography variant="label" tone="warning" className="block">
                    {formatCurrency(unallocatedAmount)} still needs to be applied.
                  </Typography>
                ) : null}
              </div>

              {invoices.length > 0 ? (
                invoices.map((invoice) => {
                  const allocationAmount = normalizeCurrencyValue(allocationValues[invoice.id] ?? 0)
                  const isSelected = allocationAmount > 0
                  const allocationState = getInvoiceAllocationState(invoice, allocationAmount)
                  const invoiceLabel = getCustomerInvoiceLabel(invoice)

                  return (
                    <div
                      key={invoice.id}
                      className={getCustomerInvoiceRowClass(
                        invoice,
                        'grid grid-cols-[auto_minmax(0,1fr)] gap-3 md:grid-cols-[auto_minmax(0,1fr)_minmax(8rem,10rem)] md:items-start'
                      )}
                    >
                      <Checkbox
                        checked={isSelected}
                        disabled={invoice.financial_state?.can_collect_payment === false}
                        aria-label={`Select ${invoiceLabel}`}
                        className="mt-1"
                        onCheckedChange={() => toggleInvoiceSelection(invoice)}
                      />
                      <div className="min-w-0">
                        <Typography variant="body" weight="medium">
                          {invoiceLabel}
                        </Typography>
                        <Typography variant="label" tone="muted">
                          {getCustomerInvoiceContactLabel(invoice)}
                        </Typography>
                        <div className="mt-3 flex flex-wrap items-end gap-x-5 gap-y-2">
                          <CustomerInvoiceDueBalance invoice={invoice} />
                          <CustomerInvoiceStatusPill invoice={invoice} />
                        </div>
                      </div>
                      <div className="col-span-2 space-y-2 md:col-span-1">
                        <Label htmlFor={`customer-payment-allocation-${invoice.id}`}>Apply</Label>
                        <CurrencyInput
                          id={`customer-payment-allocation-${invoice.id}`}
                          placeholder="0.00"
                          disabled={invoice.financial_state?.can_collect_payment === false}
                          value={allocationValues[invoice.id] ?? ''}
                          onValueChange={(value) => setInvoiceAllocation(invoice.id, value)}
                          onBlurValueChange={(value) => setInvoiceAllocation(invoice.id, value)}
                        />
                        <InvoiceAllocationOutcome state={allocationState} />
                      </div>
                    </div>
                  )
                })
              ) : (
                <EmptyState size="compact" title="No open invoices are available for payment." />
              )}
            </fieldset>
          </ScrollArea>

          <div className="space-y-4">
            <div className="rounded-md border bg-muted/20 p-4">
              <Typography variant="label" tone="muted">
                Customer
              </Typography>
              <Typography variant="h4" className="mt-1">
                {customer?.name}
              </Typography>
              <Typography variant="label" tone="muted" className="mt-2 block">
                Open balance: {formatCurrency(openBalanceTotal)}
              </Typography>
            </div>

            <div className="space-y-2 rounded-md border bg-muted/20 p-4">
              <div className="flex items-center justify-between">
                <Typography variant="label" tone="muted">
                  Invoices Selected
                </Typography>
                <Typography variant="label">{selectedAllocations.length}</Typography>
              </div>
              <div className="flex items-center justify-between">
                <Typography variant="labelStrong">Payment Total</Typography>
                <Typography variant="h4">{formatCurrency(selectedTotal)}</Typography>
              </div>
              {hasPaymentAmount ? (
                <div className="flex items-center justify-between">
                  <Typography variant="label" tone="muted">
                    Amount Entered
                  </Typography>
                  <Typography variant="label">{formatCurrency(paymentAmount)}</Typography>
                </div>
              ) : null}
            </div>

            {prepareError ? (
              <Typography variant="label" tone="destructive" role="alert">
                {prepareError}
              </Typography>
            ) : null}
            <LoadingButton
              type="button"
              className="w-full"
              disabled={!canContinue}
              isPending={isPreparingPayment}
              onClick={handleSubmit}
            >
              Continue to payment
            </LoadingButton>
          </div>
        </div>
      </DialogContent>
      {previewedPayment ? (
        <PaymentModal
          fallback={({ error, retry }) => (
            <PaymentModalLoadFallback
              error={error}
              open={Boolean(paymentModalMode)}
              onCancel={() => setPaymentModalMode(null)}
              onRetry={retry}
            />
          )}
          mode={paymentModalMode}
          onModeChange={setPaymentModalMode}
          onSuccessfulClose={handleSuccessfulPaymentClose}
          paymentSnapshot={previewedPayment.paymentSnapshot}
          paymentTarget={{
            kind: 'customer',
            customerId: customerId || null,
            allocations: previewedPayment.allocations,
            primarySalesOrderId: previewedPayment.primarySalesOrderId,
          }}
          paymentConfiguration={{
            allowedMethods: [
              ModalPaymentMethod.Reader,
              ModalPaymentMethod.CardEntry,
              ModalPaymentMethod.Manual,
            ],
            fixedAmount: parseCurrency(previewedPayment.total),
            title: 'Take Customer Payment',
            description: 'Collect the selected amount and apply it to the chosen open invoices.',
          }}
        />
      ) : null}
    </Dialog>
  )
}
